import { Link, useSearchParams } from 'react-router-dom'
import { PlayLogo } from '@/components/brand/PlayLogo'
import { AccountMenu } from '@/components/account/AccountMenu'
import { SiteFooter } from '@/components/SiteFooter'
import { PasswordSection } from '@/components/account/PasswordSection'
import { ProfileSection } from '@/components/account/ProfileSection'
import { safeReturnTo } from '@/components/onboarding/authContinuation'
import { BackLink } from '@/components/play'
import { ApiTokensSection } from '@/components/settings/ApiTokensSection'
import { useAuth } from '@/state/AuthContext'
import { isViewer, withoutOrganization } from '@/components/auth/isViewer'
import '@/components/workspace/workspace.css'
import '@/components/settings/event-settings.css'
import '@/components/account/account.css'

/** The longest name a page can pass for itself, in characters: one short line. */
const RETURN_LABEL_MAX = 60

/** Characters as people see them: grapheme clusters where the browser can split them, code points otherwise. */
function graphemes(text: string) {
  const { Segmenter } = Intl as typeof Intl & { Segmenter?: new (locale?: string, options?: { granularity: 'grapheme' }) => { segment(text: string): Iterable<{ segment: string }> } }
  return Segmenter ? Array.from(new Segmenter(undefined, { granularity: 'grapheme' }).segment(text), ({ segment }) => segment) : Array.from(text)
}

/** The name a page passed for itself (`return_label`) as one line of plain text, cut short past 60 characters; none
 *  when blank. Whitespace and control characters become single spaces. Invisible format characters (direction
 *  overrides, zero-width spaces, soft hyphens) are dropped, except the joiners and tags that hold emoji together. */
function returnLabel(value: string | null) {
  const label = value
    ?.replace(/(?![\u200c\u200d\u{e0020}-\u{e007f}])\p{Cf}/gu, '')
    .replace(/[\s\p{Cc}]+/gu, ' ')
    .trim()
  if (!label) return undefined
  const characters = graphemes(label)
  return characters.length > RETURN_LABEL_MAX ? `${characters.slice(0, RETURN_LABEL_MAX - 1).join('').trimEnd()}…` : label
}

/** The signed-in person's account: profile, password and, for hosts, API tokens. Opened from Edit profile in the
 *  account menu; `?return_to=` (an app path, such as the watch screen's) is where Back goes, and `?return_label=`
 *  (AccountMenu's `profileReturn`) names it. */
export default function SettingsPage() {
  const { user, organization } = useAuth()
  const [params] = useSearchParams()
  // Viewers have no events or API access; this page and home are theirs. A manager without an organisation has events
  // (someone else's), but no API tokens, which belong to organisations.
  const viewer = isViewer(organization, user)
  const tokens = !withoutOrganization(organization, user)
  const home = viewer ? { to: '/', name: 'StreamBridge home', back: 'StreamBridge' } : { to: '/app', name: 'Your events', back: 'Events' }
  const returnTo = safeReturnTo(params.get('return_to'), '')
  // A page that passed its own path can name itself with `return_label`; without a name its link just says Back. A
  // name without a valid path is ignored.
  const back = returnTo ? { to: returnTo, label: returnLabel(params.get('return_label')) } : { to: home.to, label: home.back }
  return (
    <div className="play-theme pw-workspace ps-page">
      <header className="pw-header">
        <Link to={home.to} aria-label={home.name}><PlayLogo /></Link>
        <div className="pw-header-right">
          <AccountMenu />
        </div>
      </header>
      <main className="ps-content">
        <BackLink to={back.to} className="pw-back">{back.label}</BackLink>
        <header className="ps-heading">
          {user && <p className="ps-event-name">{user.attributes.email}</p>}
          <h1>Your account</h1>
        </header>
        {user && (
          <div className="ps-form">
            {/* Keyed by user so the forms start from the signed-in person's saved values. */}
            <ProfileSection key={`profile-${user.id}`} user={user} />
            <PasswordSection key={`password-${user.id}`} user={user} />
            {tokens && <ApiTokensSection />}
          </div>
        )}
      </main>
      <SiteFooter />
    </div>
  )
}
